import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { fetchPluginCatalogue, GATEWAY_TYPES } from './ecaApi.js';
import { MOCK_PLUGINS } from './mock/ecaMock.js';

/**
 * React context providing the ECA plugin catalogue to all editor components.
 *
 * Fetches the catalogue from Drupal's /eca-react-editor/plugins on mount.
 * On fetch error, falls back silently to MOCK_PLUGINS so the editor remains
 * functional offline or when the backend is unavailable.
 *
 * The catalogue shape:
 *   { events: [...], conditions: [...], actions: [...], gateways: [...] }
 *
 * Each entry has the internal frontend format (configSchema as array, etc.)
 * as produced by ecaApi.adaptCatalogue().
 */

// ---------------------------------------------------------------------------
// Build mock catalogue in the same internal format as the live catalogue
// ---------------------------------------------------------------------------

const buildMockCatalogue = () => ({
  events:     MOCK_PLUGINS.events     ?? [],
  conditions: MOCK_PLUGINS.conditions ?? [],
  actions:    MOCK_PLUGINS.actions    ?? [],
  gateways:   GATEWAY_TYPES,
});

// Category key → catalogue array key mapping
const CAT_TO_KEY = {
  events:     'events',
  conditions: 'conditions',
  actions:    'actions',
  gateways:   'gateways',
};

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const CatalogueContext = createContext(null);

/**
 * Provider — fetch catalogue on mount, expose via context.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 */
export const EcaPluginCatalogueProvider = ({ children }) => {
  const [catalogue, setCatalogue] = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchPluginCatalogue()
      .then((data) => {
        if (!cancelled) {
          setCatalogue(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn('[EcaPluginCatalogueContext] Failed to load live catalogue, using mock fallback.', err);
          setCatalogue(buildMockCatalogue());
          setError(err);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, []);

  /**
   * Looks up a plugin definition by category and plugin id.
   *
   * @param {string} category  'events' | 'conditions' | 'actions' | 'gateways'
   * @param {string} pluginId  Plugin machine name.
   * @returns {object|null}
   */
  const getPluginDef = useCallback(
    (category, pluginId) => {
      const cat = catalogue?.[CAT_TO_KEY[category]];
      return cat?.find((p) => p.id === pluginId) ?? null;
    },
    [catalogue],
  );

  const value = {
    catalogue: catalogue ?? buildMockCatalogue(),
    loading,
    error,
    getPluginDef,
  };

  return (
    <CatalogueContext.Provider value={value}>
      {children}
    </CatalogueContext.Provider>
  );
};

/**
 * Hook to consume the plugin catalogue context.
 *
 * @returns {{ catalogue, loading, error, getPluginDef }}
 */
export const useCatalogue = () => {
  const ctx = useContext(CatalogueContext);
  if (!ctx) {
    throw new Error('useCatalogue must be used inside <EcaPluginCatalogueProvider>');
  }
  return ctx;
};
